import React, { useEffect, useMemo, useRef, useState } from 'react';
import DepthCanvas from '../components/DepthCanvas';
import ContactForm from '../components/ContactForm';

const DEMO_URL = 'https://voice-ai-console-781395418590.europe-west1.run.app';
const HERO_IMAGE = '/voice-ai-hero-3d.png';
const DEMO_IMAGE = '/voice-ai-demo-3d.png';
const CONTACT_IMAGE = '/voice-ai-contact-3d.png';

const depthSections = [
  {
    id: 'hero',
    kicker: 'Production Voice AI',
    title: 'Voice AI Platform for your business',
    body: 'Deploy Voice AI for conversations, workflows, and scale.'
  },
  {
    id: 'signal',
    kicker: 'Platform Architecture',
    title: 'Built for on-premise Voice AI with low latency and full control.',
    body: '',
    emphasis: 'Built for 100% on-premise deployment, low-latency execution, and scalable control.'
  },
  {
    id: 'capabilities',
    kicker: 'Voice Operations',
    title: 'Customer-facing Voice AI with workflow gravity behind it.',
    body: 'Move from conversation to action across your existing systems.',
    emphasis: 'Run on your own custom AI Agents, connect to your data, and automate your workflows.'
  },
  {
    id: 'demo',
    kicker: 'Live Demo',
    title: 'Try our live multilingual Voice AI demo.',
    body: 'Opens in a new window. Sign-in is required.',
    emphasis: 'More than 30 languagues, real-time, on-premise,low-latency conversations.'
  },
  {
    id: 'contact',
    kicker: 'Contact RareML',
    title: 'Ready to put Voice AI into production?',
    body: 'Talk to RareML about deployment, orchestration, and Voice AI for your business.'
  }
];

const signalStats = [
  { value: '24/7', label: 'always-on conversations', copy: 'Always available.' },
  { value: 'B2B', label: 'enterprise-first design', copy: 'Built for control.' }
];

const capabilityCards = [
  {
    title: 'Inbound sales qualification',
    copy: 'Capture intent and route qualified leads.'
  },
  {
    title: 'Appointment and intake automation',
    copy: 'Handle scheduling and intake with less triage.'
  },
  {
    title: 'Support and status handling',
    copy: 'Resolve routine requests and escalate edge cases.'
  }
];

const Home = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const sectionRefs = useRef([]);
  const panelRefs = useRef([]);

  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    let frame = null;

    const applyDepth = () => {
      const vh = window.innerHeight || 1;

      sectionRefs.current.forEach((section, index) => {
        const panel = panelRefs.current[index];
        if (!section || !panel) {
          return;
        }

        const rect = section.getBoundingClientRect();
        const panelCenter = rect.top + (rect.height / 2);
        const anchor = vh * 0.5;
        const signed = (panelCenter - anchor) / vh;
        const clampedSigned = Math.max(-1, Math.min(1, signed));
        const focus = 1 - Math.min(Math.abs(clampedSigned) / 0.92, 1);
        const depth = index * 34;

        panel.style.setProperty('--section-focus', focus.toFixed(4));
        panel.style.setProperty('--section-signed', clampedSigned.toFixed(4));
        panel.style.setProperty('--section-depth', `${depth}px`);
        panel.style.opacity = `${0.5 + focus * 0.5}`;
      });

      frame = null;
    };

    const onScroll = () => {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(applyDepth);
    };

    applyDepth();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const openContact = () => {
    setShowContactForm(true);
  };

  const launchDemo = () => {
    window.open(DEMO_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ position: 'relative', paddingBottom: 32 }}>
      <DepthCanvas />

      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: `linear-gradient(180deg, rgba(6, 11, 21, 0.3), rgba(6, 11, 21, 0.78)), url("${HERO_IMAGE}") right center / cover no-repeat`,
          opacity: 0.3,
          transform: 'scale(1.08)',
          filter: 'blur(6px)',
          pointerEvents: 'none',
          zIndex: -3
        }}
      />
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'radial-gradient(circle at center, rgba(131, 225, 255, 0.14), transparent 34%), linear-gradient(180deg, rgba(8, 16, 29, 0) 0%, rgba(8, 16, 29, 0.34) 28%, rgba(8, 16, 29, 0.94) 100%)',
          pointerEvents: 'none',
          zIndex: -2
        }}
      />

      <header className="site-section" style={{ paddingTop: 18 }}>
        <nav
          className="glass-card fade-up"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '14px 18px',
            borderRadius: 24,
            position: 'sticky',
            top: 14,
            zIndex: 40,
            background: 'linear-gradient(180deg, rgba(10, 20, 37, 0.82), rgba(7, 15, 27, 0.9))'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 15,
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(145deg, rgba(131, 225, 255, 0.2), rgba(255, 211, 122, 0.14))',
                border: '1px solid rgba(131, 225, 255, 0.24)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 10px 28px rgba(0,0,0,0.18)'
              }}
            >
              <span className="display-title" style={{ fontSize: '0.98rem', fontWeight: 700 }}>
                rm
              </span>
            </div>
            <div>
              <div className="display-title" style={{ fontSize: '0.96rem', fontWeight: 700 }}>
                rareml
              </div>
              <div style={{ fontSize: '0.77rem', color: 'var(--text-secondary)' }}>
                AI Voice Solutions
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button type="button" className="primary-btn" onClick={openContact}>
              Contact Us
            </button>
          </div>
        </nav>
      </header>

      <main style={{ marginTop: 7 }}>
        {depthSections.map((section, index) => (
          <section
            key={section.id}
            id={section.id}
            ref={(node) => {
              sectionRefs.current[index] = node;
            }}
            className="depth-section"
            style={{
              minHeight: '84vh',
              marginTop: index === 0 ? 0 : index === depthSections.length - 1 ? '-14vh' : '-10vh'
            }}
          >
            <div className="site-section depth-stage">
              <div
                ref={(node) => {
                  panelRefs.current[index] = node;
                }}
                className="glass-card depth-panel"
                style={{
                  position: 'relative',
                  overflow: 'visible',
                  borderRadius: 34,
                  minHeight: '68vh',
                  padding: 'clamp(22px, 4vw, 38px)',
                  background:
                    section.id === 'hero'
                      ? `linear-gradient(90deg, rgba(7, 18, 34, 0.94) 0%, rgba(7, 18, 34, 0.7) 42%, rgba(9, 23, 41, 0.3) 100%), url("${HERO_IMAGE}") center center / cover no-repeat`
                      : section.id === 'demo'
                        ? `linear-gradient(90deg, rgba(8, 18, 32, 0.94) 0%, rgba(8, 18, 32, 0.72) 44%, rgba(8, 18, 32, 0.34) 100%), url("${DEMO_IMAGE}") center center / cover no-repeat`
                        : section.id === 'contact'
                          ? `linear-gradient(90deg, rgba(8, 18, 32, 0.94) 0%, rgba(8, 18, 32, 0.72) 44%, rgba(8, 18, 32, 0.34) 100%), url("${CONTACT_IMAGE}") center center / cover no-repeat`
                          : 'linear-gradient(180deg, rgba(11, 24, 45, 0.8), rgba(8, 18, 32, 0.92))'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: index === 0
                      ? 'radial-gradient(circle at 78% 40%, rgba(131, 225, 255, 0.2), transparent 26%), radial-gradient(circle at 15% 20%, rgba(255, 211, 122, 0.08), transparent 18%)'
                      : 'radial-gradient(circle at 82% 28%, rgba(131, 225, 255, 0.14), transparent 18%), radial-gradient(circle at 18% 76%, rgba(255, 211, 122, 0.08), transparent 14%)',
                    pointerEvents: 'none'
                  }}
                />

                {section.id === 'hero' && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 24,
                      alignItems: 'center',
                      height: '100%',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <div className="fade-up">
                      <div className="section-kicker">{section.kicker}</div>
                      <h1
                        className="display-title"
                        style={{
                          fontSize: 'clamp(2.6rem, 5.6vw, 4.9rem)',
                          lineHeight: 0.94,
                          margin: '20px 0 16px',
                          maxWidth: 680
                        }}
                      >
                        {section.title}
                      </h1>
                      <p
                        style={{
                          maxWidth: 590,
                          margin: 0,
                          fontSize: '1rem',
                          lineHeight: 1.76,
                          color: 'var(--text-secondary)'
                        }}
                      >
                        {section.body}
                      </p>

                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 24 }}>
                        <button type="button" className="primary-btn" onClick={launchDemo}>
                          Live Demo
                        </button>
                      </div>

                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 24 }}>
                        {['Inbound sales qualification', 'Support and status handling'].map((item) => (
                          <span
                            key={item}
                            style={{
                              padding: '12px 18px',
                              borderRadius: 999,
                              border: '1px solid rgba(169, 183, 208, 0.18)',
                              background: 'rgba(255,255,255,0.05)',
                              color: '#d9e7fb',
                              fontSize: '0.98rem',
                              fontWeight: 600,
                              letterSpacing: '0.01em'
                            }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 24 }}>
                        {['Appointment and intake automation', 'Workflow orchestration'].map((item) => (
                          <span
                            key={item}
                            style={{
                              padding: '12px 18px',
                              borderRadius: 999,
                              border: '1px solid rgba(169, 183, 208, 0.18)',
                              background: 'rgba(255,255,255,0.05)',
                              color: '#d9e7fb',
                              fontSize: '0.98rem',
                              fontWeight: 600,
                              letterSpacing: '0.01em'
                            }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {section.id === 'signal' && (
                  <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 24, height: '100%', alignItems: 'center' }}>
                    <div>
                      <div className="section-kicker">{section.kicker}</div>
                      <h2 className="display-title" style={{ fontSize: 'clamp(2.1rem, 4vw, 3.5rem)', margin: '18px 0 14px', maxWidth: 620 }}>
                        {section.title}
                      </h2>
                      <p style={{ margin: 0, maxWidth: 590, color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: '0.98rem' }}>
                        {section.body}
                      </p>
                      {section.emphasis && (
                        <div
                          style={{
                            marginTop: 18,
                            padding: '16px 18px',
                            borderRadius: 22,
                            background: 'linear-gradient(180deg, rgba(131, 225, 255, 0.1), rgba(255, 255, 255, 0.025))',
                            border: '1px solid rgba(131, 225, 255, 0.18)',
                            color: '#e7f6ff',
                            fontSize: '0.95rem',
                            lineHeight: 1.7,
                            maxWidth: 700
                          }}
                        >
                          <strong style={{ color: '#9ae8ff' }}>Privacy & Control:</strong> {section.emphasis}
                        </div>
                      )}
                    </div>

                    <div className="signal-stack">
                      {signalStats.map((item) => (
                        <div key={item.label} className="signal-card">
                          <div className="display-title" style={{ fontSize: '2.05rem', marginBottom: 8 }}>
                            {item.value}
                          </div>
                          <div style={{ color: '#f5f7fb', fontWeight: 700, marginBottom: 6, fontSize: '0.98rem' }}>{item.label}</div>
                          <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.92rem' }}>
                            {item.copy}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {section.id === 'capabilities' && (
                  <div style={{ position: 'relative', zIndex: 1, display: 'grid', gap: 24, height: '100%', alignContent: 'center' }}>
                    <div style={{ maxWidth: 720 }}>
                      <div className="section-kicker">{section.kicker}</div>
                      <h2 className="display-title" style={{ fontSize: 'clamp(2.1rem, 4vw, 3.4rem)', margin: '18px 0 14px' }}>
                        {section.title}
                      </h2>
                      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, margin: 0, fontSize: '0.98rem' }}>
                        {section.body}
                      </p>
                      <div
                        style={{
                          marginTop: 18,
                          padding: '16px 18px',
                          borderRadius: 22,
                          background: 'linear-gradient(180deg, rgba(131, 225, 255, 0.1), rgba(255, 255, 255, 0.025))',
                          border: '1px solid rgba(131, 225, 255, 0.18)',
                          color: '#e7f6ff',
                          fontSize: '0.95rem',
                          lineHeight: 1.7,
                          maxWidth: 700
                        }}
                      >
                        <strong style={{ color: '#9ae8ff' }}>Agentic Workflows:</strong> {section.emphasis}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 18 }}>
                      {capabilityCards.map((card, cardIndex) => (
                        <article
                          key={card.title}
                          style={{
                            padding: 22,
                            borderRadius: 26,
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.025))',
                            border: '1px solid rgba(169, 183, 208, 0.16)',
                            boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                            transform: `translateY(${cardIndex * 18}px)`
                          }}
                        >
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 18,
                              marginBottom: 16,
                              display: 'grid',
                              placeItems: 'center',
                              background: 'linear-gradient(145deg, rgba(131, 225, 255, 0.16), rgba(255, 211, 122, 0.14))',
                              border: '1px solid rgba(131, 225, 255, 0.2)',
                              color: 'var(--accent)',
                              fontWeight: 800,
                              fontSize: '0.92rem'
                            }}
                          >
                            0{cardIndex + 1}
                          </div>
                          <h3 className="display-title" style={{ fontSize: '1.28rem', margin: '0 0 10px' }}>
                            {card.title}
                          </h3>
                          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.68, margin: 0, fontSize: '0.92rem' }}>
                            {card.copy}
                          </p>
                        </article>
                      ))}
                    </div>
                  </div>
                )}

                {section.id === 'demo' && (
                  <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 0.72fr)', gap: 24, height: '100%', alignItems: 'center' }}>
                    <div>
                      <div className="section-kicker">{section.kicker}</div>
                      <h2 className="display-title" style={{ fontSize: 'clamp(2.1rem, 4vw, 3.4rem)', margin: '18px 0 14px', maxWidth: 660 }}>
                        {section.title}
                      </h2>
                      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, margin: 0, maxWidth: 600, fontSize: '0.98rem' }}>
                        {section.body}
                      </p>
                      {section.emphasis && (
                        <div
                          style={{
                            marginTop: 18,
                            padding: '16px 18px',
                            borderRadius: 22,
                            background: 'linear-gradient(180deg, rgba(131, 225, 255, 0.1), rgba(255, 255, 255, 0.025))',
                            border: '1px solid rgba(131, 225, 255, 0.18)',
                            color: '#e7f6ff',
                            fontSize: '0.95rem',
                            lineHeight: 1.7,
                            maxWidth: 700
                          }}
                        >
                          <strong style={{ color: '#9ae8ff' }}>Multi-lingual:</strong> {section.emphasis}
                        </div>
                      )}
                    </div>

                    <div
                      className="glass-card"
                      style={{
                        borderRadius: 30,
                        border: '1px solid rgba(131, 225, 255, 0.18)',
                        background: 'linear-gradient(180deg, rgba(8,18,32,0.54), rgba(8,18,32,0.72))',
                        boxShadow: '0 22px 58px rgba(0,0,0,0.24)'
                      }}
                    >
                      <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(169, 183, 208, 0.14)', color: 'var(--accent)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                        Demo Access
                      </div>
                      <div style={{ padding: 18 }}>
                        <div className="display-title" style={{ fontSize: '1.55rem', marginBottom: 8 }}>
                          Voice AI
                        </div>
                        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.66, marginTop: 0, fontSize: '0.92rem' }}>
                          Redirects to the deployed demo environment. Authentication is required before entry.
                        </p>
                        <div style={{ display: 'grid', gap: 12 }}>
                          <button type="button" className="primary-btn" onClick={launchDemo}>
                            Launch Demo
                          </button>
                          <button type="button" className="secondary-btn" onClick={openContact}>
                            Start Your AI Journey
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {section.id === 'contact' && (
                  <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr', gap: 22, height: '100%', alignItems: 'center' }}>
                    <div>
                      <div className="section-kicker">{section.kicker}</div>
                      <h2 className="display-title" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', margin: '18px 0 14px', maxWidth: 650 }}>
                        {section.title}
                      </h2>
                      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.78, margin: 0, maxWidth: 620, fontSize: '0.98rem' }}>
                        {section.body}
                      </p>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 250, maxWidth: 260, marginTop: 24 }}>
                        <button type="button" className="primary-btn" onClick={openContact}>
                          Contact Us
                        </button>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            </div>
          </section>
        ))}
      </main>

      <footer className="site-section" style={{ paddingTop: 14 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
            color: 'var(--text-secondary)',
            fontSize: '0.9rem',
            padding: '8px 4px 0'
          }}
        >
          <div>© {year} RareML. All rights reserved.</div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <a href="/privacy-policy">Privacy Policy</a>
            <a href="/omniseller/delete-account">Delete Account</a>
            {/* <a href="https://linkedin.com/company/raremachinelearning" target="_blank" rel="noopener noreferrer">
              LinkedIn
            </a> */}
          </div>
        </div>
      </footer>

      {showContactForm && <ContactForm onClose={() => setShowContactForm(false)} />}

      <style>
        {`
          .depth-stage {
            position: sticky;
            top: 9vh;
            min-height: 80vh;
            display: flex;
            align-items: center;
            perspective: 2000px;
          }

          .depth-panel {
            width: 100%;
            transform:
              translate3d(
                0,
                calc(var(--section-signed, 0) * 12px),
                calc((1 - var(--section-focus, 0)) * -220px - var(--section-depth, 0px))
              )
              scale(calc(0.94 + var(--section-focus, 0) * 0.06));
            transform-origin: center center;
            transition: transform 0.16s linear, opacity 0.16s linear;
            will-change: transform, opacity;
          }

          .signal-stack {
            display: grid;
            gap: 18px;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .signal-card {
            padding: 18px 20px;
            border-radius: 24px;
            background: linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02));
            border: 1px solid rgba(169, 183, 208, 0.16);
            box-shadow: 0 18px 42px rgba(0,0,0,0.18);
            backdrop-filter: blur(10px);
          }

          @media (prefers-reduced-motion: reduce) {
            .depth-panel {
              transition: none;
              transform: none !important;
            }
          }

          @media (max-width: 1100px) {
            #hero .depth-panel > div,
            #signal .depth-panel > div,
            #demo .depth-panel > div,
            #contact .depth-panel > div {
              grid-template-columns: 1fr !important;
            }

            .signal-stack {
              grid-template-columns: 1fr;
            }

            #capabilities .depth-panel > div > div:last-child {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 768px) {
            .depth-stage {
              top: 7vh;
              min-height: 76vh;
            }

            .depth-section {
              min-height: 92vh !important;
              margin-top: -6vh !important;
            }

            .depth-panel {
              min-height: 64vh !important;
            }

            header nav {
              align-items: flex-start !important;
            }

            header nav,
            header nav > div:last-child {
              flex-direction: column;
            }
          }

          @media (max-width: 640px) {
            .section-kicker {
              font-size: 0.72rem;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Home;
